import { useQuery } from '@tanstack/react-query';
import { Users as UsersIcon, Shield, UserCheck, Eye, MessageSquare } from 'lucide-react';
import { api } from '../api/client.ts';
import { useAuth } from '../contexts/AuthContext';

const USER_COLORS = [
  '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
  '#ec4899','#06b6d4','#84cc16','#f97316','#14b8a6',
  '#6366f1','#a855f7','#e11d48','#0ea5e9','#22c55e',
  '#eab308','#f43f5e','#7c3aed','#0891b2','#65a30d',
];
const getUserColor = (userId: number) => USER_COLORS[userId % USER_COLORS.length];
function avatarInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

interface UserRow {
  id: number;
  username: string;
  email: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  facility_id: number;
  facility_name: string;
}

const ROLE_BADGE: Record<string, { label: string; color: string; bg: string; border: string }> = {
  admin:     { label: 'Admin',     color: '#f97316', bg: 'rgba(249,115,22,0.12)',  border: 'rgba(249,115,22,0.3)' },
  org_admin: { label: 'Org Admin', color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.3)' },
  manager:   { label: 'Manager',   color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)' },
  viewer:    { label: 'Viewer',    color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.3)' },
};

function RoleBadge({ role }: { role: string }) {
  const b = ROLE_BADGE[role] ?? ROLE_BADGE.viewer;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      color: b.color, background: b.bg, border: `1px solid ${b.border}`,
      whiteSpace: 'nowrap',
    }}>
      {role === 'admin' ? <Shield size={10} /> : role === 'org_admin' ? <UserCheck size={10} /> : <Eye size={10} />}
      {b.label}
    </span>
  );
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function UsersPage() {
  const { user: currentUser } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserRow[]>('/users'),
    staleTime: 30_000,
  });
  const users: UserRow[] = data?.data ?? [];

  const displayName = (u: UserRow) =>
    u.first_name ? `${u.first_name} ${u.last_name ?? ''}`.trim() : u.username;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <div className="loading-spinner" style={{ width: 32, height: 32 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
        Failed to load team members.
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <UsersIcon size={22} color="var(--accent-primary)" />
          <h2 className="page-title">Team Directory</h2>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {users.length} member{users.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              {['Name', 'Role', 'Facility', 'Last Active', ''].map((h) => (
                <th key={h} style={{
                  padding: '12px 16px', textAlign: 'left',
                  fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.id === currentUser?.id;
              const color = getUserColor(u.id);
              const initials = avatarInitials(
                u.first_name ? `${u.first_name} ${u.last_name ?? ''}`.trim() : u.username
              );
              return (
                <tr key={u.id} style={{
                  borderBottom: '1px solid var(--border-subtle)',
                  opacity: u.is_active ? 1 : 0.5,
                  background: isSelf ? 'rgba(59,130,246,0.04)' : undefined,
                }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: color + '33',
                        border: `1px solid ${color}55`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, color, flexShrink: 0,
                      }}>
                        {initials}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                          {displayName(u)}
                          {isSelf && <span style={{ marginLeft: 6, fontSize: 10, color: '#60a5fa' }}>you</span>}
                          {!u.is_active && <span style={{ marginLeft: 6, fontSize: 10, color: '#f87171' }}>inactive</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>@{u.username}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <RoleBadge role={u.role} />
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)' }}>
                    {u.facility_name}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)' }}>
                    {relativeTime(u.last_login)}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {!isSelf && (
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 12, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('openChat'));
                          window.dispatchEvent(new CustomEvent('openDirectMessage', { detail: { userId: u.id, userName: displayName(u) } }));
                        }}
                      >
                        <MessageSquare size={12} /> Message
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {users.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
            No team members found.
          </div>
        )}
      </div>
    </div>
  );
}
